import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { handleOAuthCallback } from '../services/oauthService';
import { useAuth } from '../contexts/AuthContext';
import { logger } from '../utils/logger';

/**
 * OAuth Callback Component
 * Handles the redirect from Microsoft Entra ID after user authentication
 */
const OAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const processCallback = async () => {
      try {
        // Get code and state from URL parameters
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const errorParam = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        // Check for errors from Entra ID
        if (errorParam) {
          logger.error('❌ Entra ID authentication error:', errorParam, errorDescription);
          setError(errorDescription || 'Authentication failed');
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        // Validate required parameters
        if (!code || !state) {
          setError('Invalid callback parameters');
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        // Exchange code for tokens via backend (refresh token delivered via HttpOnly cookie)
        const tokens = await handleOAuthCallback(code, state);

        // Store access token in memory via auth context
        await loginWithToken(tokens.access_token);

        // Redirect to dashboard
        navigate('/dashboard');

      } catch (err: any) {
        logger.error('❌ OAuth callback failed:', err);
        setError(err.message || 'Authentication failed. Please try again.');
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    processCallback();
  }, [searchParams, navigate, loginWithToken]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      bgcolor="#f5f5f5"
      p={3}
    >
      {error ? (
        <Box maxWidth={500} width="100%">
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Typography variant="body2" color="text.secondary" align="center">
            Redirecting to login page...
          </Typography>
        </Box>
      ) : (
        <>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h6" gutterBottom>
            Completing authentication...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait while we sign you in
          </Typography>
        </>
      )}
    </Box>
  );
};

export default OAuthCallback;
